import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getStaticPageMeta,
  isArticleDetailPath,
  NOT_FOUND_PAGE_META,
  normalizePathname,
} from '../../config/staticPageMeta';
import { ADMIN_PAGE_PATH } from '../../constants/adminRoute';
import { ORGANIZATION, SITE_DEFAULT_ORIGIN } from '../../config/siteOrganization';

function absoluteUrl(path: string, origin: string): string {
  try {
    return new URL(path, `${origin}/`).href;
  } catch {
    return path;
  }
}

/**
 * 記事詳細・管理画面以外のルートで、タブ名・description・OGP / Twitter をページに合わせる。
 * {element} より後に置き、ルート遷移時の useLayoutEffect 順を制御しやすくする。
 */
export default function StaticRouteDocumentHead() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const adminBase = `/${ADMIN_PAGE_PATH.replace(/^\/+/, '')}`;
    if (pathname === adminBase || pathname.startsWith(`${adminBase}/`)) {
      return;
    }
    if (isArticleDetailPath(pathname)) {
      return;
    }

    const norm = normalizePathname(pathname);
    const meta = getStaticPageMeta(norm) ?? NOT_FOUND_PAGE_META;
    const origin = SITE_DEFAULT_ORIGIN.replace(/\/$/, '');
    const canonicalUrl = norm === '/' ? `${origin}/` : `${origin}${norm}`;
    const pageUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}${norm === '/' ? '/' : norm}`
        : canonicalUrl;
    const ogImage = absoluteUrl('/apple-touch-icon.png', origin);

    document
      .querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')
      .forEach((n) => n.remove());

    document.title = meta.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', meta.description);
    }

    const head = document.head;
    const ogSite = ORGANIZATION.name;

    const addOg = (property: string, content: string) => {
      const m = document.createElement('meta');
      m.setAttribute('property', property);
      m.setAttribute('content', content);
      head.appendChild(m);
    };
    const addTw = (name: string, content: string) => {
      const m = document.createElement('meta');
      m.setAttribute('name', name);
      m.setAttribute('content', content);
      head.appendChild(m);
    };

    addOg('og:title', meta.title);
    addOg('og:description', meta.description);
    addOg('og:url', pageUrl);
    addOg('og:type', 'website');
    addOg('og:image', ogImage);
    addOg('og:site_name', ogSite);
    addTw('twitter:card', 'summary_large_image');
    addTw('twitter:title', meta.title);
    addTw('twitter:description', meta.description);
    addTw('twitter:image', ogImage);
  }, [pathname]);

  return null;
}
